import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { Typography } from '@mui/material';
import {
  Flex,
  FlexSpace,
  Menu,
  IconButton,
} from '@elementary/common-ui/components';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import GenerateAIButton from '@elementary/common-ui/components/GenerateAIButton';
import { ReactComponent as MoreIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as CatalogIcon } from '@elementary/common-ui/assets/20x20/catalog.svg';
import { ReactComponent as LineageIcon } from '@elementary/common-ui/assets/20x20/lineage.svg';
import { ReactComponent as TestIcon } from '@elementary/common-ui/assets/16x16/test.svg';
import {
  INCIDENT_ID,
  TREE_NODE,
} from '@elementary/common-ui/constants/urlParams';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useNavigateWithEnvParam from '@elementary/common-ui/hooks/useNavigateWithEnvParam';
import { getIncidentDisplayLabel } from 'incidents/utils';
import useIncidentDetails from '../hooks/useIncidentDetails';

const IncidentOverviewDrawerTitle = () => {
  const [searchParams] = useSearchParams();
  const { data: account } = useAccount();
  const { openSidePanel } = useSidePanelParams();
  const navigate = useNavigateWithEnvParam();

  const incidentId = searchParams.get(INCIDENT_ID);
  const { data: incident } = useIncidentDetails(incidentId ?? undefined);

  const isTestIncident = incident?.sourceType === 'test';
  const isPipelineIncident = incident?.sourceType === 'pipeline';
  const assetId = incident?.tableAssetId;
  const testId = incident?.sourceId;

  const handleMenuChange = (value: string) => {
    if (!incident) return;

    switch (value) {
      case 'lineage':
        if (assetId) {
          const lineageParams = new URLSearchParams();
          lineageParams.set(TREE_NODE, JSON.stringify({ id: assetId }));
          navigate(`/report/lineage?${lineageParams.toString()}`);
        }
        break;
      case 'catalog':
        if (assetId) {
          const catalogParams = new URLSearchParams();
          catalogParams.set(TREE_NODE, JSON.stringify({ id: assetId }));
          navigate(`/catalog?${catalogParams.toString()}`);
        }
        break;
      case 'test_overview':
        if (testId) {
          openSidePanel('test', testId, 'test');
        }
        break;
    }
  };

  const menuOptions: Option[] = [
    ...(assetId
      ? [
          {
            value: 'lineage',
            label: 'Show asset in lineage',
            icon: <LineageIcon />,
          },
          {
            value: 'catalog',
            label: 'Show asset in catalog',
            icon: <CatalogIcon />,
          },
        ]
      : []),
    ...(isTestIncident && testId
      ? [
          {
            value: 'test_overview',
            label: 'Show test overview',
            icon: <TestIcon />,
          },
        ]
      : []),
  ];

  return (
    <FlexSpace sx={{ width: '100%', alignItems: 'center', pr: 1 }}>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
        Incident Overview
      </Typography>
      <Flex sx={{ gap: 1, alignItems: 'center' }}>
        <GenerateAIButton
          suggestions={
            isPipelineIncident
              ? ['Find the root cause']
              : ['Find the root cause', 'Show downstream impact']
          }
          context={
            incidentId
              ? {
                  incidentIds: [incidentId],
                  displayLabels: incident?.displayName
                    ? [
                        getIncidentDisplayLabel(
                          incident.displayName,
                          incident.testSubType,
                        ),
                      ]
                    : undefined,
                }
              : undefined
          }
          disabled={!account?.ai_features_enabled}
        >
          Investigate with AI
        </GenerateAIButton>

        {menuOptions.length > 0 && (
          <Menu
            menuButton={
              <IconButton>
                <MoreIcon />
              </IconButton>
            }
            options={menuOptions}
            onChange={handleMenuChange}
            closeOnChange
          />
        )}
      </Flex>
    </FlexSpace>
  );
};

export default IncidentOverviewDrawerTitle;
